import React, { useState, useEffect } from "react";
import axios from "axios";
import { Box, Divider, Flex, Icon, Spinner, Stack, Text } from "@chakra-ui/react";
import { MdError } from "react-icons/md";
import Card from "horizon/components/card/Card";
import StatusHeader from "./StatusHeader";
import ElasticInfoSection from "./ElasticInfoSection";
import OptimizedIndexSection from "./OptimizedIndexSection";
import RedisCacheSection from "./RedisCacheSection";
import DatabaseSection from "./DatabaseSection";

/**
 * Fetches the ElasticSearch debug status from the API.
 * @param {Function} setLoading - Setter for loading state
 * @param {Function} setData - Setter for status data
 * @param {Function} setError - Setter for error message
 * @param {Function|null} onDataUpdate - Optional callback with fresh data
 * @returns {Promise<void>}
 */
async function fetchStatus(setLoading, setData, setError, onDataUpdate) {
    setLoading(true);
    setError(null);
    try {
        const response = await axios.get("/service_search/debug/status");
        setData(response.data);
        if (onDataUpdate) onDataUpdate(response.data);
    } catch (err) {
        console.error("Error fetching status:", err);
        setError("Unable to retrieve ElasticSearch status");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the error state with icon and message.
 * @param {object} props
 * @param {string} props.error - Error message string
 * @returns {React.ReactElement}
 */
function ErrorState({ error }) {
    return (
        <Flex direction="column" align="center" gap="12px" py="32px">
            <Icon as={MdError} w="48px" h="48px" color="red.500" />
            <Text fontSize="sm" color="secondaryGray.600" textAlign="center">{error}</Text>
        </Flex>
    );
}

/**
 * Renders the main data sections: Elastic info, optimized index, Redis, and database.
 * @param {object} props
 * @param {object} props.data - Full status response data
 * @returns {React.ReactElement}
 */
function StatusSections({ data }) {
    return (
        <Stack spacing="20px">
            <ElasticInfoSection elasticsearch={data?.elasticsearch} />
            <Divider />
            <OptimizedIndexSection elasticsearch={data?.elasticsearch} />
            <Divider />
            <RedisCacheSection redis={data?.redis} />
            <Divider />
            <DatabaseSection database={data?.database} />
        </Stack>
    );
}

/**
 * Renders the body of the status card with loading, error, or data states.
 * @param {object} props
 * @param {boolean} props.loading - Whether data is loading
 * @param {string|null} props.error - Error message if any
 * @param {object|null} props.data - Status data
 * @returns {React.ReactElement}
 */
function StatusCardBody({ loading, error, data }) {
    if (loading) {
        return <Flex justify="center" py="32px"><Spinner color="brand.500" /></Flex>;
    }
    if (error) {
        return <ErrorState error={error} />;
    }
    return <StatusSections data={data} />;
}

/**
 * Status monitoring card for ElasticSearch, Redis, and database.
 * Fetches status on mount and when refreshTrigger changes.
 * @param {object} props
 * @param {*} props.refreshTrigger - Value change triggers a data refresh
 * @param {Function} props.onRefresh - Callback for the refresh button
 * @param {Function} [props.onDataUpdate] - Optional callback receiving fresh data
 * @returns {React.ReactElement}
 */
function ElasticStatusCard({ refreshTrigger, onRefresh, onDataUpdate }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchStatus(setLoading, setData, setError, onDataUpdate);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [refreshTrigger]);

    return (
        <Card h="100%">
            <StatusHeader loading={loading} onRefresh={onRefresh} />
            <Box>
                <StatusCardBody loading={loading} error={error} data={data} />
            </Box>
        </Card>
    );
}

export default ElasticStatusCard;
